import React from "react";
import { fetchInfraErrors } from "../../services/infraApi";
import { isMobileViewport } from "../../utils/responsive";
import { formatKst } from "../../utils/time";

class ErrorLogPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            errors: [],
            allExchanges: [],
            loading: true,
            error: null,
            level: "",
            exchange: "",
            search: "",
            autoRefresh: (() => {
                const stored = localStorage.getItem('backtest_error_log_autoRefresh');
                return stored === null ? true : stored === 'true';
            })(),
        };
        this._interval = null;
        this._debounceTimer = null;
    }

    componentDidMount() {
        this.fetchData();
        if (this.state.autoRefresh) {
            this._interval = setInterval(this.fetchData, 5000);
        }
    }

    componentWillUnmount() {
        if (this._interval) {
            clearInterval(this._interval);
        }
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
        }
    }

    fetchData = () => {
        const { level, exchange, search } = this.state;
        const params = { limit: 100 };
        if (level) params.level = level;
        if (exchange) params.exchange = exchange;
        if (search) params.search = search;

        fetchInfraErrors(params)
            .then((res) => {
                const data = res?.data;
                const list = Array.isArray(data) ? data : data?.errors || [];

                // 필터가 모두 비어있을 때(전체 조회) 거래소 목록 캐싱
                if (!level && !exchange && !search) {
                    const exchanges = [...new Set(list.filter((e) => e.exchange).map((e) => e.exchange))].sort();
                    this.setState({ errors: list, allExchanges: exchanges, loading: false, error: null });
                } else {
                    this.setState({ errors: list, loading: false, error: null });
                }
            })
            .catch((err) => {
                this.setState({ error: err?.message || "로그 조회 실패", loading: false });
            });
    };

    handleFilterChange = (field, value) => {
        this.setState({ [field]: value }, () => {
            if (this._debounceTimer) {
                clearTimeout(this._debounceTimer);
            }
            this._debounceTimer = setTimeout(this.fetchData, 300);
        });
    };

    toggleAutoRefresh = () => {
        this.setState(
            (prev) => {
                const next = !prev.autoRefresh;
                localStorage.setItem('backtest_error_log_autoRefresh', String(next));
                return { autoRefresh: next };
            },
            () => {
                if (this.state.autoRefresh) {
                    if (this._interval) clearInterval(this._interval);
                    this._interval = setInterval(this.fetchData, 5000);
                } else {
                    if (this._interval) {
                        clearInterval(this._interval);
                        this._interval = null;
                    }
                }
            }
        );
    };

    getLevelClass(level) {
        if (level === "ERROR") return "text-loss font-semibold";
        if (level === "WARN") return "text-warning font-semibold";
        if (level === "INFO") return "text-brand-light";
        return "text-content-secondary";
    }

    getUniqueExchanges() {
        const { allExchanges, errors } = this.state;
        if (allExchanges.length > 0) {
            return allExchanges;
        }
        // fallback: 현재 에러 목록에서 추출
        const set = new Set();
        errors.forEach((e) => {
            if (e.exchange) set.add(e.exchange);
        });
        return Array.from(set).sort();
    }

    formatTime(isoString) {
        return formatKst(isoString);
    }

    renderMobileCard = (item, idx) => {
        const borderClass =
            item.level === "ERROR" ? "border-l-2 border-loss" :
            item.level === "WARN"  ? "border-l-2 border-warning" :
            item.level === "INFO"  ? "border-l-2 border-brand" :
                                     "border-l-2 border-surface-border";

        const badgeClass =
            item.level === "ERROR" ? "bg-loss-subtle text-loss" :
            item.level === "WARN"  ? "bg-warning-subtle text-warning" :
            item.level === "INFO"  ? "bg-brand-subtle text-brand-light" :
                                     "bg-surface-hover text-content-secondary";

        return (
            <div
                key={item.id || idx}
                className={`rounded-xl border border-surface-border bg-surface-elevated p-3 animate-fade-in ${borderClass}`}
            >
                <div className="flex items-center justify-between gap-2">
                    <span className="font-num text-xs text-content-muted">
                        {this.formatTime(item.timestamp || item.created_at)}
                    </span>
                    <div className="flex items-center gap-1.5">
                        {item.exchange && (
                            <span className="rounded-md bg-surface-hover px-1.5 py-0.5 text-xs font-semibold text-content-secondary">
                                {item.exchange}
                            </span>
                        )}
                        {item.symbol && (
                            <span className="rounded-md bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light">
                                {item.symbol}
                            </span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}>
                            {item.level || "-"}
                        </span>
                    </div>
                </div>

                <p className="mt-2 text-xs leading-relaxed text-content">
                    {item.message || "-"}
                </p>

                {(item.error || item.error_detail || item.detail || item.traceback || item.caller || item.symbol || (item.fields && Object.keys(item.fields).length > 0)) && (
                    <details className="group mt-2">
                        <summary className="flex cursor-pointer items-center gap-1 text-xs text-content-muted transition-colors hover:text-content-secondary">
                            <span className="transition-transform group-open:rotate-90">▸</span>
                            에러 상세 보기
                        </summary>
                        <div className="mt-1.5 max-h-48 overflow-auto rounded-lg border border-surface-border bg-surface p-2.5 text-xs leading-relaxed text-content shadow-inner-light space-y-1">
                            {(item.error || item.error_detail || item.detail || item.traceback) && (
                                <div><span className="font-semibold text-content-secondary">에러:</span> {item.error || item.error_detail || item.detail || item.traceback}</div>
                            )}
                            {item.caller && (
                                <div><span className="font-semibold text-content-secondary">호출 위치:</span> <code className="text-xs">{item.caller}</code></div>
                            )}
                            {item.symbol && (
                                <div><span className="font-semibold text-content-secondary">심볼:</span> {item.symbol}</div>
                            )}
                            {item.fields && Object.keys(item.fields).length > 0 && (
                                <div>
                                    <span className="font-semibold text-content-secondary">추가 필드:</span>
                                    <pre className="mt-0.5 whitespace-pre-wrap break-all font-mono text-xs">{JSON.stringify(item.fields, null, 2)}</pre>
                                </div>
                            )}
                        </div>
                    </details>
                )}
            </div>
        );
    };

    render() {
        const { errors, loading, error, level, exchange, search, autoRefresh } = this.state;
        const uniqueExchanges = this.getUniqueExchanges();
        const isMobile = isMobileViewport();

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold text-content">에러 로그</h2>
                    <button
                        type="button"
                        onClick={this.toggleAutoRefresh}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            autoRefresh
                                ? "border-gain/40 bg-gain-subtle text-gain"
                                : "border-surface-border text-content-secondary hover:bg-surface-hover"
                        }`}
                    >
                        {autoRefresh ? "자동 갱신 중" : "자동 갱신 중지됨"}
                    </button>
                </div>

                {/* 필터 바 */}
                <div className="mt-4 flex flex-wrap gap-2">
                    <select
                        aria-label="로그 레벨 필터"
                        value={level}
                        onChange={(e) => this.handleFilterChange("level", e.target.value)}
                        className="rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content focus:border-brand focus:outline-none"
                    >
                        <option value="">전체 레벨</option>
                        <option value="ERROR">ERROR</option>
                        <option value="WARN">WARN</option>
                        <option value="INFO">INFO</option>
                    </select>

                    <select
                        aria-label="거래소 필터"
                        value={exchange}
                        onChange={(e) => this.handleFilterChange("exchange", e.target.value)}
                        className="rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content focus:border-brand focus:outline-none"
                    >
                        <option value="">전체 거래소</option>
                        {uniqueExchanges.map((ex) => (
                            <option key={ex} value={ex}>
                                {ex}
                            </option>
                        ))}
                    </select>

                    <input
                        type="text"
                        aria-label="메시지 검색"
                        placeholder="메시지 검색..."
                        value={search}
                        onChange={(e) => this.handleFilterChange("search", e.target.value)}
                        className="min-w-0 flex-1 rounded-lg border border-surface-border bg-surface px-3 py-2 text-sm text-content placeholder-content-muted focus:border-brand focus:outline-none sm:min-w-44"
                    />
                </div>

                {/* 에러 상태 */}
                {error && (
                    <div className="mt-4 rounded-lg border border-loss/30 bg-loss/10 px-4 py-3 text-sm text-loss">
                        {error}
                    </div>
                )}

                {/* 데스크톱: 테이블 */}
                {!isMobile && (
                    <div className="mt-4 overflow-x-auto rounded-xl border border-surface-border">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-surface-elevated text-xs uppercase tracking-wide text-content-secondary">
                                <tr>
                                    <th className="whitespace-nowrap px-4 py-3">시간</th>
                                    <th className="whitespace-nowrap px-4 py-3">레벨</th>
                                    <th className="whitespace-nowrap px-4 py-3">거래소</th>
                                    <th className="whitespace-nowrap px-4 py-3">심볼</th>
                                    <th className="px-4 py-3">메시지</th>
                                    <th className="px-4 py-3">에러 상세</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-content-secondary">
                                            불러오는 중...
                                        </td>
                                    </tr>
                                )}
                                {!loading && errors.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-6 text-center text-content-muted">
                                            표시할 에러 로그가 없습니다.
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    errors.map((item, idx) => (
                                        <tr key={item.id || idx} className="hover:bg-surface-hover">
                                            <td className="whitespace-nowrap px-4 py-2.5 text-xs text-content-secondary">
                                                {this.formatTime(item.timestamp || item.created_at)}
                                            </td>
                                            <td className={`whitespace-nowrap px-4 py-2.5 text-xs ${this.getLevelClass(item.level)}`}>
                                                {item.level || "-"}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-2.5 text-xs text-content">
                                                {item.exchange || "-"}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-2.5 text-xs text-content">
                                                {item.symbol || "-"}
                                            </td>
                                            <td className="max-w-xs px-4 py-2.5 text-xs text-content">
                                                <span className="line-clamp-2">{item.message || "-"}</span>
                                            </td>
                                            <td className="max-w-xs px-4 py-2.5 text-xs text-content-secondary">
                                                {(item.error || item.error_detail || item.detail || item.traceback || item.caller || (item.fields && Object.keys(item.fields).length > 0)) ? (
                                                    <details>
                                                        <summary className="cursor-pointer text-content-muted hover:text-content-secondary">
                                                            상세 보기
                                                        </summary>
                                                        <div className="mt-1 rounded bg-surface-elevated p-2 text-xs text-content space-y-1">
                                                            {(item.error || item.error_detail || item.detail || item.traceback) && (
                                                                <div><span className="font-semibold text-content-secondary">에러:</span> {item.error || item.error_detail || item.detail || item.traceback}</div>
                                                            )}
                                                            {item.caller && (
                                                                <div><span className="font-semibold text-content-secondary">호출 위치:</span> <code className="text-xs">{item.caller}</code></div>
                                                            )}
                                                            {item.fields && Object.keys(item.fields).length > 0 && (
                                                                <div>
                                                                    <span className="font-semibold text-content-secondary">추가 필드:</span>
                                                                    <pre className="mt-0.5 whitespace-pre-wrap break-all font-mono text-xs">{JSON.stringify(item.fields, null, 2)}</pre>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </details>
                                                ) : (
                                                    <span className="text-content-muted">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 모바일: 카드 뷰 */}
                {isMobile && (
                    <div className="mt-4 space-y-2">
                        {loading && (
                            <div className="py-6 text-center text-sm text-content-secondary">불러오는 중...</div>
                        )}
                        {!loading && errors.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="text-2xl text-content-muted">✓</div>
                                <div className="mt-2 text-sm font-semibold text-content-secondary">
                                    표시할 에러 로그가 없습니다
                                </div>
                                <div className="mt-1 text-xs text-content-muted">
                                    시스템이 정상 동작 중입니다
                                </div>
                            </div>
                        )}
                        {!loading && errors.map((item, idx) => this.renderMobileCard(item, idx))}
                    </div>
                )}

                {!loading && errors.length > 0 && (
                    <div className="mt-2 text-right text-xs text-content-muted">
                        최대 100개 표시 중 · 총 {errors.length}개
                    </div>
                )}
            </div>
        );
    }
}

export default ErrorLogPanel;
